import { camelizeKeys } from "humps";
import { join, pipe, reduce, toPairs } from "ramda";
import { HQ_SERVICE, INVENTORY } from "app_constants";
import type { PARTNER_INTEGRATION_VALUES } from "helpers/chrome/locations";
import { checkStatus, getRequest, parseJSON } from "helpers/requests";
import {
  type IBrandGroup,
  sortEntitiesByBrand,
} from "helpers/transforms/entities";

export enum SubscriptionLevel {
  CORE = "Core",
  PRO = "Pro",
  ENTERPRISE = "Enterprise",
}

// All of these arguments are optional. For non super users, the backend ignores
// all of them. For super users, the backend will attempt to look up all of the
// locations associated with the query. It first tries to find all of the
// hierarchical siblings of the store, then falls back to the brand if it cannot
// find any, and finally falls back to the merchant group.
export interface IEntitiesQuery {
  storeKey?: string;
  brandKey?: string;
  merchantGroupKey?: string;
}

export type AppLocation =
  | "swipely"
  | "breadcrumb"
  | "inventory"
  | "recurly"
  | "workforce_scheduling";

export interface IEntityApp {
  enabled?: boolean;
  isAdmin?: boolean;
  name: AppLocation;
  permissions?: { [permission: string]: string | boolean | number | null };
  storeKey: string;
}

export interface IEntity {
  apps: IEntityApp[];
  brandId: string;
  brandName: string;
  createdAt?: string;
  customReports: boolean;
  upserveOlo: boolean;
  activePosInstallationName: string;
  id: string;
  name: string;
  omahaMid: string;
  partnerIntegrations?: IPartnerIntegrations;
  salesforceAccountId: string;
  subscriptionLevel: SubscriptionLevel;
  timezone?: string;
  workWeekStartDay: number;
  tradingDay?: string;
}

export interface IMerchantGroup {
  hqKey: string | null;
  salesforceId: string;
}

// "b" brand, "s" store, "g" the merchant-group scope for the Menu/Group
// Management pages. Under "g" the vendorLocation is either "all" — the whole
// merchant group, every location across all brands unioned (the default scope;
// see helpers/merchant_group_workspace) — or a custom location group's slug (a
// cross-brand cohort; see helpers/location_groups).
export type VendorLevel = "b" | "s" | "g";

interface IPartnerIntegrations {
  [integration: string]: PARTNER_INTEGRATION_VALUES;
}

function getEntities(query: IEntitiesQuery = {}): Promise<IBrandGroup[]> {
  const { storeKey, brandKey, merchantGroupKey } = query;

  const params = pipe<
    {
      [index: string]: string;
    },
    string[][],
    string[],
    string
  >(
    toPairs,
    reduce(
      (paramArr, [key, value]) =>
        value
          ? paramArr.concat(
              `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
            )
          : paramArr,
      [] as string[]
    ),
    join("&")
  )({
    store_key: storeKey || "",
    brand_key: brandKey || "",
    merchant_group_key: merchantGroupKey || "",
  });

  return fetch(`${HQ_SERVICE}/locations.json?${params}`, getRequest())
    .then(checkStatus)
    .then(parseJSON)
    .then((entities) => camelizeKeys(entities) as IEntity[])
    .then(sortEntitiesByBrand);
}

function getNewInventoryLocation(id: string): Promise<Response> {
  return fetch(
    `${INVENTORY}/?act=switch_client&client_id=customer_${id}`,
    getRequest()
  );
}
export { getEntities, getNewInventoryLocation };
